// The mark that names an armed pick mode, on the WebGL2 engine (R469, R500,
// ui-map-interface.md §8 and §6.3).
//
// A pick mode is a modal state: the client waits for the player to name a
// target, and the next click is an order and not a selection. The mark is a
// "+" with the middle missing — four arms on the two axes, with a gap
// between them — in the colour of the order it arms. The gap keeps the mark
// off the point it names, so the player reads the water under the target
// rather than a line over it.
//
// The geometry is `render/crosshair-paths.ts`, the one description of the
// shape. Nothing here re-states a figure of `presentation.toml`.
//
// **This layer is world space with screen sizes**, because a crosshair must
// hold one size at every zoom. The mark rides the world batches and takes
// `F_SCREEN`: the segment shader divides a width by the zoom, and the
// circle shader divides both the radius and the width. The arm endpoints are the one thing neither shader scales,
// so `set` places them at the screen offsets divided by the frame's zoom.
//
// The cost is one comparison per frame. `fast/main.ts` calls `set()` from
// the frame, because the DOM overlay writes `ui.hunting` in its own action
// handler and nothing tells the map. A `set()` that repeats its last four
// arguments packs nothing (§10.1, §10.3), and a frame at an unchanged zoom
// re-flushes the instances the last pack wrote.
//
// The shape has ONE owner: `crosshairAtOrigin` builds the mark of each mode
// once, around the origin, and a pack moves it into place. A pack allocates
// nothing.

import { FLEET_CROSSHAIR } from '../../gen/presentation';
import { crosshairAtOrigin, type CrosshairMode, type CrosshairPaths } from '../../render/crosshair-paths';
import { F_SCREEN, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, SegmentSink } from './batches';
import { zoomOf } from './shared';

export type { CrosshairMode };

/**
 * The arms of one crosshair at the world point `(x, y)`, in the line width
 * and the alpha of `presentation.toml`. `k` is one over the zoom: the arm
 * ends are the one thing the segment shader does not scale, so the screen
 * offsets of the path are divided by the zoom here. The drag order preview
 * draws its mark with this as well.
 */
export function packArms(
  s: SegmentSink, arms: CrosshairPaths['arms'], x: number, y: number, k: number, color: number,
): void {
  const cfg = FLEET_CROSSHAIR;
  for (let i = 0; i < arms.length; i++) {
    const [x0, y0, x1, y1] = arms[i];
    s.line(x + x0 * k, y + y0 * k, x + x1 * k, y + y1 * k,
      cfg.width, color, cfg.alpha, F_SCREEN);
  }
}

/** The four arms, and the ring the aim and the snap share. */
const MAX_ARMS = 4;
const MAX_RINGS = 1;

export class CrosshairLayer implements Layer {
  readonly name = 'crosshair';

  private readonly segments: SegmentSink;
  private readonly circles: CircleSink;
  private shown = true;

  // The last mark asked for. The frame repeats itself while the pointer is
  // still, so these four fields are the whole reason the layer is cheap.
  private mode: CrosshairMode = null;
  private x = 0;
  private y = 0;
  private snapped = false;

  /** Set by `set` and `clear`; cleared by the pack that answers them. */
  private dirty = false;
  /** The zoom the packed instances were placed at. The arms ride it. */
  private zoom = 0;
  /** How many times the layer packed. The tests read it. */
  packs = 0;

  constructor(src: BatchSource) {
    this.segments = src.newSegments(MAX_ARMS);
    this.circles = src.newCircles(MAX_RINGS);
  }

  /**
   * Draw the mark for an armed order at a WORLD point, or clear on `null`.
   *
   * `snapped` says the order point took a structure instead of the pixel
   * under the cursor. The mark then wears its snap ring, so the player sees
   * the snap and does not read the order as a miss.
   */
  set(mode: CrosshairMode, x: number, y: number, snapped: boolean): void {
    if (mode === null) { this.clear(); return; }
    if (mode === this.mode && x === this.x && y === this.y && snapped === this.snapped) return;
    this.mode = mode;
    this.x = x;
    this.y = y;
    this.snapped = snapped;
    this.dirty = true;
  }

  /** Nothing is armed: the layer draws nothing, and a repeat costs nothing. */
  clear(): void {
    if (this.mode === null) return;
    this.mode = null;
    this.snapped = false;
    this.dirty = true;
  }

  /** What the layer is drawing, for the tests and for `__debug`. */
  get armed(): CrosshairMode { return this.mode; }

  update(frame: FrameInfo): void {
    const zoom = frame.cam.zoom;
    // The arms are placed in world px, so a zoom change moves them even
    // where the mode and the point did not. Everything else the two shaders
    // scale on their own.
    if (!this.dirty && zoom === this.zoom) return;
    this.dirty = false;
    this.zoom = zoom;
    this.pack(zoom);
  }

  draw(ctx: DrawCtx): void {
    this.segments.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  private pack(zoom: number): void {
    const s = this.segments;
    const c = this.circles;
    s.begin();
    c.begin();
    this.packs++;
    const mode = this.mode;
    if (mode === null) return;
    const cfg = FLEET_CROSSHAIR;
    // The shape is asked for around the origin and moved into world px
    // here, so the screen offsets of `presentation.toml` reach the world
    // exactly once and this module states none of them.
    const path = crosshairAtOrigin(mode, this.snapped);
    const x = this.x;
    const y = this.y;
    packArms(s, path.arms, x, y, 1 / zoomOf(zoom), path.color);
    // The aim's ring and the snap ring are the same instance at two sizes,
    // exactly as the PixiJS layer strokes one circle or the other.
    if (path.ringR > 0) {
      c.ring(x, y, path.ringR, cfg.aimRingWidth, path.color, cfg.aimRingAlpha, F_SCREEN);
    } else if (path.snapR > 0) {
      c.ring(x, y, path.snapR, cfg.snapRingWidth, path.color, cfg.snapRingAlpha, F_SCREEN);
    }
  }
}
